import React from 'react';
import {Bolt, GppGood, SentimentDissatisfied, SentimentSatisfied, SentimentSatisfiedAlt, Star, VerifiedUser} from '@mui/icons-material';
import {Tooltip} from '@mui/material';

export type OfferDTO = {
  id: string;
  item: any;
  steamAccount: any;
  priceMinor: number;
  status: string;
  offerCreationDate: string | null;
  sellerTradeCount: number;
  sellerSuccessfulTradesCount: number;
  sellerMedianDeliveryTime: number;
  sellerSince: string; // ISO string
  sellerIsOnline: boolean;
  sellerLastSeen: string; // ISO string
  sellerKycApproved: boolean;
  isItemOwner: boolean;
};

interface Props {
  offer: OfferDTO;
}

const SellerDetails: React.FC<Props> = ({offer}) => {
  // Format delivery time for the tooltip
  const formatDeliveryTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  // Format dates
  const formatDate = (dateString: string, includeTime: boolean = true) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(includeTime ? {hour: '2-digit', minute: '2-digit'} : {}),
    });
  };

  // Logic for delivery speed bolts
  const renderBolts = () => {
    const hasTrades = offer.sellerSuccessfulTradesCount > 0;
    const time = offer.sellerMedianDeliveryTime;
    let activeBolts = 0;

    if (hasTrades) {
      if (time < 10) activeBolts = 3;
      else if (time < 30) activeBolts = 2;
      else if (time < 120) activeBolts = 1;
    }

    const tooltipTitle = hasTrades
        ? `Median delivery: ${formatDeliveryTime(offer.sellerMedianDeliveryTime)}`
        : 'Delivery speed unknown';

    return (
        <Tooltip title={tooltipTitle} arrow>
          <div
              className={`flex bg-neutral-200/50 dark:bg-black/20 px-2 py-1 rounded border border-black/5 dark:border-white/5 items-center ${!hasTrades ? 'opacity-30' : ''}`}>
            {[1, 2, 3].map((i) => (
                <Bolt
                    key={i}
                    sx={{
                      fontSize: 14,
                      color: i <= activeBolts ? '#3b82f6' : '#374151',
                      marginLeft: i > 1 ? -0.5 : 0
                    }}
                />
            ))}
          </div>
        </Tooltip>
    );
  };

  // Get color and icon based on success rate
  const getRatingInfo = () => {
    if (offer.sellerTradeCount === 0) return {label: 'N/A', color: 'text-gray-500', Icon: SentimentSatisfied};
    const rate = (offer.sellerSuccessfulTradesCount / offer.sellerTradeCount) * 100;
    if (rate > 92) return {label: `${rate.toFixed(0)}%`, color: 'text-green-500', Icon: SentimentSatisfiedAlt};
    if (rate > 80) return {label: `${rate.toFixed(0)}%`, color: 'text-yellow-500', Icon: SentimentSatisfied};
    return {label: `${rate.toFixed(0)}%`, color: 'text-red-500', Icon: SentimentDissatisfied};
  };

  const rating = getRatingInfo();
  const showStar = offer.sellerTradeCount >= 100;
  const hasTrades = offer.sellerSuccessfulTradesCount > 0;

  // Profile Icon Component with integrated Status Dot and Tooltip
  const SellerAvatar = () => {
    const statusTooltipTitle = offer.sellerIsOnline
        ? 'Seller is currently online'
        : `Seller is offline. Last seen: ${formatDate(offer.sellerLastSeen)}`;

    return (
        <div className="relative">
          <Tooltip title={offer.sellerKycApproved ? 'KYC Verified: Identity officially confirmed' : 'Not KYC Verified'} arrow>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${
                offer.sellerKycApproved
                    ? 'bg-blue-600/10 border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
                    : 'bg-neutral-500/10 border-neutral-500/20 opacity-40'
            }`}>
              {offer.sellerKycApproved ? (
                  <GppGood className="text-blue-500" sx={{fontSize: 22}}/>
              ) : (
                  <VerifiedUser className="text-neutral-500" sx={{fontSize: 20}}/>
              )}
            </div>
          </Tooltip>

          {/* Status Dot with Tooltip, positioned on the avatar corner */}
          <Tooltip title={statusTooltipTitle} arrow placement="top">
            <div
                className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-3 h-3 bg-neutral-100 dark:bg-neutral-900 rounded-full cursor-pointer">
              <div className="relative flex h-2 w-2 items-center justify-center">
                {/* Outer ring - darker green if online, darker neutral if offline */}
                <span className={`absolute inline-flex h-3.5 w-3.5 rounded-full ${offer.sellerIsOnline ? 'bg-green-900/40' : 'bg-neutral-800'}`}></span>

                {/* Inner dot - light green if online, light neutral if offline */}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${offer.sellerIsOnline ? 'bg-green-400' : 'bg-neutral-400'}`}></span>
              </div>
            </div>
          </Tooltip>
        </div>
    );
  };

  return (
      <div
          className="bg-neutral-100/70 dark:bg-neutral-900/40 p-4 rounded border border-neutral-200 dark:border-white/5 flex flex-col justify-center gap-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SellerAvatar/>

            <div className="flex flex-col">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className={`text-[9px] font-black uppercase tracking-[0.15em] leading-none ${
                    offer.sellerKycApproved ? 'text-blue-500' : 'text-neutral-500/60'
                }`}>
                  {offer.sellerKycApproved ? 'KYC Verified' : 'Not KYC Verified'}
                </span>

                {showStar && (
                    <Tooltip title={`Experienced Seller: ${offer.sellerSuccessfulTradesCount} trades`} arrow>
                      <Star sx={{fontSize: 12, color: '#eab308'}}/>
                    </Tooltip>
                )}
              </div>

              <div className="flex flex-col">
                <span className="text-sm font-black text-neutral-900 dark:text-white leading-tight">
                  Seller Profile
                </span>
                <div className="flex flex-col mt-0.5">
                  <span className="text-[10px] text-neutral-400 dark:text-gray-500 font-medium leading-none mb-1">
                    Member since {formatDate(offer.sellerSince, false)}
                  </span>
                  {offer.sellerIsOnline ? (
                      <span className="text-[10px] text-green-500 font-bold uppercase tracking-tight leading-none">
                        Online
                      </span>
                  ) : (
                      <span className="text-[10px] text-neutral-400 dark:text-gray-500 font-medium leading-none">
                        Last seen: {formatDate(offer.sellerLastSeen)}
                      </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {renderBolts()}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Sales Count */}
          <div className="bg-neutral-200/50 dark:bg-black/20 px-3 py-1.5 rounded border border-black/5 dark:border-white/5 flex justify-between items-center">
            <span className="text-[9px] text-neutral-500 dark:text-gray-500 uppercase font-black">Sales</span>
            <span className="text-xs font-bold text-neutral-800 dark:text-gray-200">{offer.sellerSuccessfulTradesCount}</span>
          </div>

          {/* Rating - Faded if unknown */}
          <Tooltip title={hasTrades ? `${offer.sellerSuccessfulTradesCount} / ${offer.sellerTradeCount} trades` : 'No rating yet'} arrow>
            <div
                className={`bg-neutral-200/50 dark:bg-black/20 px-3 py-1.5 rounded border border-black/5 dark:border-white/5 flex justify-between items-center cursor-pointer ${!hasTrades ? 'opacity-30' : ''}`}>
              <span className="text-[9px] text-neutral-500 dark:text-gray-500 uppercase font-black">Rating</span>
              <div className="flex items-center gap-1">
                <rating.Icon sx={{fontSize: 12}} className={rating.color}/>
                <span className={`text-xs font-bold ${rating.color}`}>{rating.label}</span>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
  );
};

export default SellerDetails;